<template>
  <Card :id="elemId" :class="'box-' + type" style="margin-top: 10px;">
    <template #header v-if="title || $slots.header || $slots.actions">
      <div :class="['box-header-wrapper', 'box-header-' + type]">
        <slot name="header">
          <div class="box-header-title">
            <i v-if="icon" :class="icon" style="margin-right: 0.5rem;"></i>
            {{ title }}
          </div>
        </slot>
        <div v-if="$slots.actions" class="box-header-actions">
          <slot name="actions"></slot>
        </div>
      </div>
    </template>
    <template #content>
      <Message v-if="validation().length > 0" severity="error" :closable="false">
        <div v-for="errorMsg in validation()" :key="errorMsg">
          {{ errorMsg }}
        </div>
      </Message>
      <slot></slot>
    </template>
  </Card>
</template>

<script setup lang="ts">
import Card from 'primevue/card';
import Message from 'primevue/message';

type BoxType = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary';

withDefaults(defineProps<{
  type?: BoxType;
  elemId?: string;
  title?: string;
  icon?: string;
  validation?: () => string[];
}>(), {
  type: 'default',
  validation: () => []
});
</script>

<style scoped>
/* Base header styling */
.box-header-wrapper {
  padding: 1rem 1.5rem;
  margin: 0;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.box-header-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.box-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Remove PrimeVue's default header padding */
:deep(.p-card-header) {
  padding: 0 !important;
}

/* Type-based styling - apply background colors to wrapper */
.box-header-default {
  background-color: transparent;
}

/* Primary Type */
.box-header-primary {
  background-color: var(--app-primary-color);
}

.box-header-primary .box-header-title {
  color: var(--app-secondary-color);
}

/* Success Type */
.box-header-success {
  background-color: var(--app-success-color);
}

.box-header-success .box-header-title {
  color: white;
}

/* Warning Type */
.box-header-warning {
  background-color: var(--app-warning-color);
}

.box-header-warning .box-header-title {
  color: white;
}

/* Danger Type */
.box-header-danger {
  background-color: var(--app-danger-color);
}

.box-header-danger .box-header-title {
  color: white;
}

/* Info Type */
.box-header-info {
  background-color: var(--app-info-color);
}

.box-header-info .box-header-title {
  color: white;
}

/* Secondary Type */
.box-header-secondary {
  background-color: var(--app-secondary-color);
}

.box-header-secondary .box-header-title {
  color: var(--app-primary-color);
}

/* Make validation messages stack vertically */
:deep(.p-message) {
  margin-bottom: 1rem;
}

:deep(.p-message > div) {
  display: block;
}
</style>
